import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import PortraitStage from '../components/PortraitStage';
import SuccessScanModal from '../components/modals/SuccessScanModal';
import FailureScanModal from '../components/modals/FailureScanModal';
import ExpiredModal from '../components/modals/ExpiredModal';
import { verifyTicketStatusApi } from '../api/ticket';
import {
  DEFAULT_EXPIRED_TICKET_MESSAGE,
  DEFAULT_INVALID_MESSAGE,
  getExtendTicketUrl,
  getTicketIdFromScan,
  isTicketExpired,
  isTicketExtendable,
  pickTicket,
  saveTicket,
} from '../utils/ticket';
import qrTicketIcon from '../assets/img/qr-.png';

function ScanBackIcon() {
  return (
    <svg width="108" height="108" viewBox="0 0 108 108" fill="none" aria-hidden="true">
      <g filter="url(#scanBackShadow)">
        <rect x="4" width="100" height="100" rx="50" fill="white" shapeRendering="crispEdges" />
        <path
          d="M30.5 49.5L28.4671 47.4671L26.4341 49.5L28.4671 51.5329L30.5 49.5ZM73.625 52.375C75.2128 52.375 76.5 51.0878 76.5 49.5C76.5 47.9122 75.2128 46.625 73.625 46.625V49.5V52.375ZM47.75 32.25L45.7171 30.2171L28.4671 47.4671L30.5 49.5L32.5329 51.5329L49.7829 34.2829L47.75 32.25ZM30.5 49.5L28.4671 51.5329L45.7171 68.7829L47.75 66.75L49.7829 64.7171L32.5329 47.4671L30.5 49.5ZM30.5 49.5V52.375H73.625V49.5V46.625H30.5V49.5Z"
          fill="#DC2863"
        />
      </g>
      <defs>
        <filter id="scanBackShadow" x="0" y="0" width="108" height="108" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="4" />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 10v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="7.5" r="1" fill="currentColor" />
    </svg>
  );
}

const SCAN_IDLE_MS = 20000;
const READER_ID = 'social-collage-qr-reader';

function ScanScreen() {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const startIdRef = useRef(0);
  const busyRef = useRef(false);
  const handleScanRef = useRef(async () => {});
  const [cameraError, setCameraError] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [scanPaused, setScanPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successScan, setSuccessScan] = useState(false);
  const [hasScannedError, setHasScannedError] = useState(false);
  const [expiredModal, setExpiredModal] = useState(false);
  const [expiredTicketId, setExpiredTicketId] = useState('');
  const [message, setMessage] = useState(DEFAULT_INVALID_MESSAGE);
  const [resumeKey, setResumeKey] = useState(0);

  const pauseReaderVideo = useCallback(() => {
    const video = document.querySelector(`#${READER_ID} video`);
    if (!video) return;
    try {
      video.pause();
    } catch {
      /* ignore */
    }
    try {
      const stream = video.srcObject;
      if (stream && typeof stream.getTracks === 'function') {
        stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch {
            /* ignore */
          }
        });
      }
      video.srcObject = null;
      video.removeAttribute('src');
      video.load();
    } catch {
      /* ignore */
    }
  }, []);

  const stopCamera = useCallback(async () => {
    startIdRef.current += 1;
    const scanner = scannerRef.current;
    scannerRef.current = null;
    setCameraReady(false);

    // Pause / detach media before clear so in-flight video.play() does not surface AbortError.
    pauseReaderVideo();

    if (!scanner) return;
    try {
      if (scanner.isScanning) await scanner.stop();
    } catch {
      /* already stopped */
    }
    try {
      scanner.clear();
    } catch {
      /* ignore */
    }
  }, [pauseReaderVideo]);

  const startCamera = useCallback(async () => {
    await stopCamera();
    const startId = startIdRef.current;
    setCameraError(false);
    setScanPaused(false);

    if (!document.getElementById(READER_ID)) return;

    const scanner = new Html5Qrcode(READER_ID, { verbose: false });
    scannerRef.current = scanner;

    const onDetected = (decodedText) => {
      if (busyRef.current) return;
      busyRef.current = true;
      handleScanRef.current(decodedText);
    };

    const config = { fps: 10, aspectRatio: 1 };
    const isStale = () =>
      startId !== startIdRef.current || scannerRef.current !== scanner;

    const startSafe = async (cameraConfig) => {
      try {
        await scanner.start(cameraConfig, config, onDetected, () => {});
      } catch (error) {
        // html5-qrcode may reject with AbortError when play() is interrupted by stop/clear.
        if (error?.name === 'AbortError' || isStale()) return false;
        throw error;
      }
      return !isStale();
    };

    try {
      const started = await startSafe({ facingMode: 'environment' });
      if (!started) {
        if (isStale()) {
          try {
            if (scanner.isScanning) await scanner.stop();
            scanner.clear();
          } catch {
            /* ignore */
          }
        }
        return;
      }
    } catch {
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (!cameras.length) throw new Error('No camera');
        if (isStale()) {
          try {
            scanner.clear();
          } catch {
            /* ignore */
          }
          return;
        }
        const started = await startSafe(cameras[0].id);
        if (!started) {
          if (isStale()) {
            try {
              if (scanner.isScanning) await scanner.stop();
              scanner.clear();
            } catch {
              /* ignore */
            }
          }
          return;
        }
      } catch (error) {
        if (error?.name === 'AbortError' || isStale()) {
          try {
            scanner.clear();
          } catch {
            /* ignore */
          }
          return;
        }
        if (startId === startIdRef.current) {
          scannerRef.current = null;
          try {
            scanner.clear();
          } catch {
            /* ignore */
          }
          setCameraError(true);
        }
        return;
      }
    }

    if (isStale()) {
      pauseReaderVideo();
      try {
        if (scanner.isScanning) await scanner.stop();
        scanner.clear();
      } catch {
        /* ignore */
      }
      return;
    }

    setCameraReady(true);
  }, [pauseReaderVideo, stopCamera]);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  const scanAgain = useCallback(() => {
    busyRef.current = false;
    setScanPaused(false);
    setHasScannedError(false);
    setExpiredModal(false);
    setSuccessScan(false);
    setResumeKey((count) => count + 1);
    startCamera();
  }, [startCamera]);

  const resumeScan = useCallback(() => {
    busyRef.current = false;
    setHasScannedError(false);
    setExpiredModal(false);
    setSuccessScan(false);
    setResumeKey((count) => count + 1);
  }, []);

  const scanBusy = isLoading || successScan || hasScannedError || expiredModal;

  useEffect(() => {
    if (!cameraReady || scanPaused || scanBusy) return undefined;
    const timer = window.setTimeout(() => {
      busyRef.current = true;
      setScanPaused(true);
      stopCamera();
    }, SCAN_IDLE_MS);
    return () => window.clearTimeout(timer);
  }, [cameraReady, resumeKey, scanBusy, scanPaused, stopCamera]);

  const showFailure = useCallback((text) => {
    setMessage(text || DEFAULT_INVALID_MESSAGE);
    setHasScannedError(true);
  }, []);

  const handleScan = useCallback(
    async (decodedText) => {
      const ticketId = getTicketIdFromScan(decodedText);
      if (!ticketId) {
        showFailure(DEFAULT_INVALID_MESSAGE);
        return;
      }

      setIsLoading(true);
      try {
        const response = await verifyTicketStatusApi({ ticketId });
        const raw = response?.data || {};
        const data =
          raw.ticket || raw.validate != null || raw.isExpired != null
            ? raw
            : raw.data && typeof raw.data === 'object'
              ? raw.data
              : raw;
        const ticket = pickTicket(data);

        if (isTicketExpired(data)) {
          const expiredText = data.message || DEFAULT_EXPIRED_TICKET_MESSAGE;
          if (isTicketExtendable(data)) {
            setExpiredTicketId(ticket?.id || ticketId);
            setMessage(expiredText);
            setExpiredModal(true);
          } else {
            showFailure(expiredText);
          }
          return;
        }

        const isValid =
          Boolean(ticket?.id) &&
          (data.validate === true || (data.validate === undefined && !isTicketExpired(data)));

        if (!isValid) {
          showFailure(data.message || DEFAULT_INVALID_MESSAGE);
          return;
        }

        saveTicket(ticket);
        setSuccessScan(true);
        window.setTimeout(() => {
          navigate('/add-contribution', { state: { ticket } });
        }, 1200);
      } catch (err) {
        const errorData = err?.response?.data || {};
        const errorMessage = errorData.message || err?.message || '';
        const mentionsExpired =
          typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('expired');

        if (isTicketExpired(errorData) || mentionsExpired) {
          const expiredText = errorMessage || DEFAULT_EXPIRED_TICKET_MESSAGE;
          if (isTicketExtendable(errorData)) {
            setExpiredTicketId(errorData.ticket?.id || ticketId);
            setMessage(expiredText);
            setExpiredModal(true);
          } else {
            showFailure(expiredText);
          }
        } else {
          showFailure(errorMessage || DEFAULT_INVALID_MESSAGE);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, showFailure]
  );

  handleScanRef.current = handleScan;

  return (
    <PortraitStage className="portraitViewport--fill">
      <section className="scanScreen" aria-label="Scan QR ticket">
        <button type="button" className="scanScreen__back" aria-label="Back" onClick={() => navigate('/')}>
          <ScanBackIcon />
        </button>
        <div className="scanScreen__hint">
          <InfoIcon />
          Please make sure the QR Code is clearly visible to the camera
        </div>

        <div className={`scanScreen__camera${scanPaused ? ' is-paused' : ''}`}>
          <div id={READER_ID} className="scanScreen__reader" />
          {cameraError ? (
            <p className="scanScreen__denied">Camera permission is needed to scan a ticket.</p>
          ) : null}
          {!scanPaused && !cameraError ? <span className="scanLine" aria-hidden="true" /> : null}
          <div className="scanScreen__frame" aria-hidden="true">
            <span className="scanScreen__corner scanScreen__corner--tl" />
            <span className="scanScreen__corner scanScreen__corner--tr" />
            <span className="scanScreen__corner scanScreen__corner--bl" />
            <span className="scanScreen__corner scanScreen__corner--br" />
          </div>
          {scanPaused || cameraError ? (
            <>
              <div className="scanScreen__overlay" aria-hidden="true" />
              <button type="button" className="scanScreen__again" onClick={scanAgain}>
                Scan again
              </button>
            </>
          ) : null}
        </div>

        <div className="scanScreen__card">
          <span className="scanScreen__cardIcon">
            <img src={qrTicketIcon} alt="" />
          </span>
          <div className="scanScreen__cardText">
            <p className="scanScreen__cardTitle">Show your QR ticket</p>
            <p className="scanScreen__cardSub">
              Hold your QR ticket steady in front of the camera.
            </p>
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="scanLoader" role="status">
          Verifying ticket…
        </div>
      ) : null}

      <SuccessScanModal show={successScan} onHide={() => setSuccessScan(false)} />

      <FailureScanModal
        show={hasScannedError}
        message={message}
        onHide={resumeScan}
      />

      <ExpiredModal
        show={expiredModal}
        extendTicketUrl={getExtendTicketUrl(expiredTicketId)}
        onHide={resumeScan}
      />
    </PortraitStage>
  );
}

export default ScanScreen;
